// import '@maxpreps/shared/corecomponents/PerfTest/whyDidYouRenderScript';
import useRequestContext from '@maxpreps/shared/contexts/RequestContext';
import { TeamContextProvider } from '@maxpreps/shared/contexts/TeamContext';
import AppWrapper from '@maxpreps/shared/corecomponents/App/AppWrapper';
import ErrorBoundary from '@maxpreps/shared/corecomponents/ErrorBoundary/ErrorBoundary';
import GlobalLayout from '@maxpreps/shared/corecomponents/GlobalLayout/GlobalLayout';
import Head from '@maxpreps/shared/corecomponents/Head/Head';
import SchoolFooter from '@maxpreps/shared/corecomponents/School/SchoolFooter';
import { setDefaultToastOptions } from '@maxpreps/shared/corecomponents/Toastify/Toast';
import ZendeskScript from '@maxpreps/shared/corecomponents/ZendeskScript/ZendeskScript';
import { getSchoolColors } from '@maxpreps/shared/functions/primitiveHelpers/colorHelpers';
import { AppType } from '@maxpreps/shared/functions/utils/requestContextUtils';
import { formatSchoolName } from '@maxpreps/shared/functions/utils/schoolUtils';
import useIsAdmin from '@maxpreps/shared/hooks/useIsAdmin';
import { AppProps, IPageMetaProps } from '@maxpreps/shared/models/pageTypes';
import React, { useMemo } from 'react';
import BackToPublicBanner from '../components/shared/Admin/BackToPublicBanner';
import TeamHeader from '../components/shared/TeamHeader';
import TeamNavBar from '../components/shared/TeamNavBar';
import projectConfig from '../project.meta';
import { ITeamPageConfig } from '../utils/ITeamPageConfig';
import { Page } from '../utils/pages';
import { ITeamAppServerSideProps } from './../data/appGetServerSideProps';

// ghost 7

// if (typeof window !== 'undefined' && process.env.APP_ENV === 'development') {
//   whyDidYouRender(React);
// }

export interface ITeamAppProps extends IPageMetaProps, ITeamAppServerSideProps {
  pageConfig: ITeamPageConfig;
}

const App: React.FC<AppProps<ITeamAppProps>> = ({ Component, pageProps }) => {
  const {
    teamContext,
    breadcrumbListData,
    siteNavData,
    pageTitle,
    pageDescription,
    canonicalUrl,
    pageConfig,
    ...restOfPageProps
  } = pageProps;

  const isAdmin = useIsAdmin();
  const { appType } = useRequestContext();

  const isFrameVisible =
    appType === AppType.Website && (pageConfig?.isFrameVisible ?? true);

  const memoizedSubheaders = useMemo(() => {
    const subheaders: any = [];

    if (isFrameVisible) {
      subheaders.push(
        {
          key: 'team',
          zIndex: 103,
          component: <TeamHeader pageConfig={pageConfig} />
        },
        {
          sticky: true,
          height: 52, // Mobile height (44) set in GlobalHeader since we can't pivot on isMobile here
          key: 'teamNav',
          zIndex: 101,
          component: <TeamNavBar navId={pageConfig?.navId || ''} />
        }
      );
    }

    if (isAdmin && pageConfig?.adminConfig?.isBannerVisible) {
      subheaders.push({
        key: 'back_to_public',
        zIndex: 101,
        height: 55,
        sticky: true,
        component: (
          <BackToPublicBanner
            pageKey={pageConfig?.adminConfig?.publicPageKey ?? pageConfig.page}
          />
        )
      });
    }

    return subheaders;
  }, [pageConfig, isAdmin, isFrameVisible]);

  const {
    schoolColor1,
    schoolColor2,
    schoolColor3,
    schoolCanonicalUrl,
    schoolName,
    schoolMascotUrl,
    schoolAddress,
    schoolZipCode,
    schoolShouldAppendHighSchoolString,
    stateCode,
    schoolCity
  } = teamContext?.data || {};

  const schoolColors = getSchoolColors(
    schoolColor1,
    schoolColor2,
    schoolColor3
  );

  const theme = {
    colors: {
      school: schoolColors,
      nav: {
        isSiteNavDark: true,
        header: schoolColors.primary,
        headerDark: schoolColors.primaryDark,
        footer: schoolColors.primary,
        footerDark: schoolColors.primaryDark
      }
    }
  };

  const breadcrumbConfig = {
    data: breadcrumbListData,
    pageName:
      pageConfig?.page === Page.Home
        ? undefined
        : `${teamContext?.data?.formattedSportSeasonName} ${pageConfig?.pageName}`
  };

  setDefaultToastOptions({
    progressStyle: {
      background: schoolColors.primary
    }
  });

  return (
    <TeamContextProvider initContextValue={teamContext}>
      {isAdmin && <ZendeskScript />}
      <GlobalLayout
        theme={theme}
        isFrameVisible={isFrameVisible}
        breadcrumbConfig={breadcrumbConfig}
        subheaders={memoizedSubheaders}
        siteNavData={siteNavData}
      >
        <ErrorBoundary includeLayoutManager>
          <Head
            title={pageTitle}
            description={pageDescription}
            canonical={canonicalUrl}
          />
          <Component {...restOfPageProps} pageConfig={pageConfig} />

          {isFrameVisible && (
            <>
              <SchoolFooter
                city={schoolCity}
                state={stateCode}
                name={formatSchoolName(schoolName, undefined, undefined, {
                  includeTermHighSchool: schoolShouldAppendHighSchoolString
                })}
                zip={schoolZipCode}
                address={schoolAddress}
                mascotUrl={schoolMascotUrl}
                canonicalUrl={schoolCanonicalUrl}
              />
            </>
          )}
        </ErrorBoundary>
      </GlobalLayout>
    </TeamContextProvider>
  );
};

const _AppWrapper = props => (
  <AppWrapper projectConfig={projectConfig} {...props}>
    <App {...props}></App>
  </AppWrapper>
);
export default _AppWrapper;
